import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../api/client'
import { ProductCard } from '../components/ProductCard'
import { CartDrawer } from '../components/CartDrawer'
import { useCart } from '../contexts/CartContext'

interface Store {
  id: string
  name: string
  address: string
  zona?: string
  phone?: string
  schedule?: string
  coverage_km?: number
  has_delivery: boolean
}

interface Product {
  id: string
  name: string
  description?: string
  price: number
  sku?: string
  image_url?: string
  is_available: boolean
}

function ProductSkeleton() {
  return (
    <div style={{ borderRadius: 16, overflow: 'hidden', border: '1px solid var(--border)' }}>
      <div className="skeleton" style={{ height: 180 }} />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div className="skeleton" style={{ height: 16, borderRadius: 4 }} />
        <div className="skeleton" style={{ height: 12, borderRadius: 4, width: '60%' }} />
        <div className="skeleton" style={{ height: 24, borderRadius: 4, width: '40%', marginTop: 8 }} />
      </div>
    </div>
  )
}

export function StorePage() {
  const { id } = useParams<{ id: string }>()
  const { itemCount } = useCart()

  const [store, setStore] = useState<Store | null>(null)
  const [products, setProducts] = useState<Product[]>([])
  const [search, setSearch] = useState('')
  const [loadingStore, setLoadingStore] = useState(true)
  const [loadingProducts, setLoadingProducts] = useState(true)
  const [error, setError] = useState('')
  const [cartOpen, setCartOpen] = useState(false)

  useEffect(() => {
    if (!id) return
    api.get<Store>(`/api/stores/${id}`)
      .then(setStore)
      .catch(err => setError(err.message))
      .finally(() => setLoadingStore(false))
  }, [id])

  useEffect(() => {
    if (!id) return
    setLoadingProducts(true)
    const params = new URLSearchParams()
    params.set('available_only', 'false')
    params.set('limit', '100')
    if (search) params.set('search', search)

    api.get<Product[]>(`/api/stores/${id}/products?${params.toString()}`)
      .then(setProducts)
      .catch(err => setError(err.message))
      .finally(() => setLoadingProducts(false))
  }, [id, search])

  if (loadingStore) {
    return (
      <div className="loading-state" style={{ padding: '80px' }}>
        <div className="skeleton" style={{ height: 200, borderRadius: 0 }} />
      </div>
    )
  }
  if (error && !store) return <div className="alert alert-error" style={{ margin: '40px auto', maxWidth: 600 }}>{error}</div>
  if (!store) return null

  const waHref = store.phone ? `https://wa.me/${store.phone.replace(/\D/g, '')}` : null

  return (
    <main className="store-page">
      {/* Store header — dark */}
      <div className="store-page-header">
        <div className="store-page-header-inner">
          <div className="store-page-info">
            <h1 className="store-page-name">{store.name}</h1>
            <p className="store-page-address">
              📍 {store.address}{store.zona ? `, Zona ${store.zona}` : ''}
            </p>
            {store.schedule && (
              <p className="store-page-schedule">🕐 {store.schedule}</p>
            )}
            {waHref && (
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="store-page-phone"
              >
                📱 {store.phone}
              </a>
            )}
          </div>
          <div className="store-page-badge">
            {store.has_delivery ? (
              <span className="badge badge-green" style={{ fontSize: '.85rem', padding: '6px 14px' }}>
                🚚 Delivery hasta {store.coverage_km ?? '?'} km
              </span>
            ) : (
              <span className="badge badge-gray" style={{ fontSize: '.85rem', padding: '6px 14px' }}>
                🏪 Solo en tienda
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Products section */}
      <div className="section">
        <div className="section-inner">
          <div className="store-products-toolbar">
            <h2 className="section-title left">Productos</h2>
            <input
              type="search"
              className="products-search"
              placeholder="🔍 Buscar producto o SKU..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ maxWidth: 320 }}
            />
          </div>

          {error && <div className="alert alert-error" style={{ marginTop: 16 }}>{error}</div>}

          {loadingProducts ? (
            <div className="product-grid">
              {[1,2,3,4,5,6].map(i => <ProductSkeleton key={i} />)}
            </div>
          ) : products.length === 0 ? (
            <div className="empty-state animate-in">
              <div className="empty-icon">📦</div>
              <p>{search ? 'No se encontraron productos' : 'Esta tienda aún no tiene productos'}</p>
            </div>
          ) : (
            <div className="product-grid">
              {products.map(p => (
                <ProductCard
                  key={p.id}
                  {...p}
                  storeId={store.id}
                  storeName={store.name}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Cart button */}
      {itemCount > 0 && (
        <button
          type="button"
          className="cart-fab"
          onClick={() => setCartOpen(true)}
        >
          🛒 Ver carrito ({itemCount})
        </button>
      )}

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </main>
  )
}
